import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useOrgKey } from '@/hooks/auth/useOrgKey';
import type { AgentLabel } from '@/lib/types';

/**
 * The org's label catalog — the kinds of work a run can be — and the
 * mutations that maintain it.
 *
 * Most labels arrive without anyone touching these: the prompt classifier
 * applies them as it titles a new run. What is left for the UI is the rest of
 * the lifecycle — adding a label the vocabulary is missing, correcting one the
 * classifier got wrong, and taking one off a run.
 *
 * Like groups, a membership write changes both the catalog (counts) and the
 * agents (their chips), and touches no agent row for the WebSocket to carry,
 * so these invalidate the agent queries directly.
 */

const AGENT_LIST_KEY_PREFIXES = ['agents', 'pinned-agents', 'agent-search', 'agent'] as const;

function useInvalidateLabelData() {
  const queryClient = useQueryClient();
  const orgKey = useOrgKey();
  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.agentLabels(orgKey) });
    for (const prefix of AGENT_LIST_KEY_PREFIXES) {
      void queryClient.invalidateQueries({ queryKey: [prefix, orgKey] });
    }
  }, [queryClient, orgKey]);
}

/**
 * Every label in the active org, in catalog order, with how many agents wear
 * it. The first read of an organization seeds the built-in vocabulary
 * server-side, so this never comes back empty on a fresh tenant.
 */
export function useAgentLabels() {
  const orgKey = useOrgKey();
  const key = useMemo(() => [...queryKeys.agentLabels(orgKey)], [orgKey]);
  return useQuery<AgentLabel[]>({
    queryKey: key,
    queryFn: async () => {
      const res = await api.listAgentLabels();
      return res?.labels ?? [];
    },
  });
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError && typeof error.body.error === 'string') return error.body.error;
  return fallback;
}

/** Add a label to the vocabulary, optionally applying it straight away. */
export function useCreateAgentLabel() {
  const invalidate = useInvalidateLabelData();
  return useMutation({
    mutationFn: async (input: { name: string; description?: string; agentIds?: string[] }) => {
      const res = await api.createAgentLabel(input);
      return res.label;
    },
    onSuccess: (label) => {
      invalidate();
      toast.success(
        label.memberCount > 0
          ? `Created ${label.name} and applied it to ${countLabel(label.memberCount)}`
          : `Created ${label.name}`,
      );
    },
    onError: (error) => toast.error(errorMessage(error, 'Failed to create label')),
  });
}

export function useApplyAgentLabel() {
  const invalidate = useInvalidateLabelData();
  return useMutation({
    mutationFn: async (input: { labelId: string; agentIds: string[] }) => {
      const res = await api.applyAgentLabel(input.labelId, input.agentIds);
      return res.label;
    },
    onSuccess: (label, input) => {
      invalidate();
      toast.success(`Labelled ${countLabel(input.agentIds.length)} ${label.name}`);
    },
    onError: (error) => toast.error(errorMessage(error, 'Failed to apply the label')),
  });
}

export function useRemoveAgentLabel() {
  const invalidate = useInvalidateLabelData();
  return useMutation({
    mutationFn: async (input: { labelId: string; agentIds: string[] }) => {
      const res = await api.removeAgentLabel(input.labelId, input.agentIds);
      return res.label;
    },
    onSuccess: (label, input) => {
      invalidate();
      toast.success(`Removed ${label.name} from ${countLabel(input.agentIds.length)}`);
    },
    onError: (error) => toast.error(errorMessage(error, 'Failed to remove the label')),
  });
}

function countLabel(count: number): string {
  return `${count} agent${count === 1 ? '' : 's'}`;
}
